// Library Methods
import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";

// Components
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import Loader from "src/components/shared/loaders/Loader/Loader";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import LiveUpdateButtons from "./LiveUpdateButtons/LiveUpdateButtons";
import LiveUpdateAlerts from "./LiveUpdateAlerts/LiveUpdateAlerts";
import LiveUpdateSettings from "./LiveUpdateSettings/LiveUpdateSettings";
import LiveUpdateStepper from "./LiveUpdateStepper/LiveUpdateStepper";
import LiveUpdateContent from "./LiveUpdateContent/LiveUpdateContent";
import PackageDrawer from "./LiveUpdatePackageInfo/PackageDrawer/PackageDrawer";
import IconButton from "@mui/material/IconButton";
import { EyeIcon, MaximizeIcon, MinimizeIcon } from "src/assets/icons";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

// Utils
import {
  getExamPackageText,
  getExamSectionEye,
  isMultiExamPackage,
} from "src/utils/exams/examReportsHelper";
import { getElapsedTime } from "src/utils/exams/examReportsHelper";
import { setupTheme } from "src/utils/theme/themeHelper";

// Styles
import {
  ThemeProvider as LightThemeProvider,
  useTheme,
} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  getSurfaceTokens,
  ModalCustomCancel,
} from "src/styles/mui/muiStylesHelper";

// Config
import { priorityLanguageMap } from "src/config/languages";

type LiveUpdateModalType = {
  connection: HubConnection | null;
  isLoadingSignalR: boolean;
  refetchExams: () => Promise<void>;
};

const LiveUpdateModal = ({
  connection,
  isLoadingSignalR,
  refetchExams,
}: LiveUpdateModalType) => {
  const { t, i18n } = useTranslation();
  const { refetchDevices } = useDevicesContext();

  const {
    exam,
    currentExamSection,
    device,
    open,
    examStatus,
    examReportLive,
    isVrMode,
    isVideo,
    isIOLSimulation,
    isProgressiveSimulation,
    fullScreenModal,
    peerConnection,
    shouldDisplayNavigation,
    actions,
  } = useLiveUpdateContext();

  const {
    handleNavigate,
    handleSubscribeToDevice,
    handleUnsubscribeFromDevice,
    setFullScreenModal,
    stopWebRTC,
    setIsRightCompleted,
    setIsTerminated,
    setIsNotStarted,
    setIsPaused,
    setIsConfirmation,
    handleCloseModal,
    setIsVrMode,
  } = actions;

  const theme = useTheme();
  const { elevatedSurface } = getSurfaceTokens(theme);
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));
  const showFullScreenModal = smallerThanSm || fullScreenModal;

  // The live-update view (device video feed, exam graphs) isn't styled for
  // dark mode, so it's locked to light regardless of the app's theme --
  // same approach as the report pages and ComparisonImage.
  const languageKey = i18n.language as keyof typeof priorityLanguageMap;
  const lightTheme = useMemo(
    () => setupTheme(theme.direction, languageKey, "light"),
    [theme.direction, languageKey]
  );

  const elapsedTime = useMemo(() => {
    if (examReportLive?.timing?.elapsed)
      return getElapsedTime(examReportLive.timing.elapsed);

    return "00:00";
  }, [examReportLive?.timing?.elapsed]);

  const patientName = useMemo(
    () =>
      exam?.patient?.firstName && exam?.patient?.lastName
        ? `${exam.patient.firstName} ${exam.patient.lastName}`
        : "",
    [exam?.patient]
  );

  const isMultiPackage = useMemo(
    () => Boolean(exam && isMultiExamPackage(exam)),
    [exam]
  );

  // description for the currently-tested exam
  const currentExamDescription = useMemo(
    () => getExamPackageText(currentExamSection, t),
    [currentExamSection, t]
  );

  const currentEye = useMemo(
    () => getExamSectionEye(currentExamSection, t),
    [currentExamSection, t]
  );

  useEffect(() => {
    if (!open) return;

    setIsNotStarted(examStatus?.status === t("Not Started"));
    setIsPaused(examStatus?.status === t("Paused"));
    setIsConfirmation(examStatus?.status === t("Confirmation"));
    setIsRightCompleted(examStatus?.status === t("Right Eye Completed"));
    setIsTerminated(examStatus?.status === t("Terminated"));
  }, [
    t,
    examStatus?.status,
    open,
    setIsConfirmation,
    setIsNotStarted,
    setIsPaused,
    setIsRightCompleted,
    setIsTerminated,
  ]);

  useEffect(() => {
    // stop WebRtc if not a video exam or in VR mode
    if (
      !isVrMode &&
      !isVideo &&
      !isIOLSimulation &&
      !isProgressiveSimulation &&
      peerConnection
    )
      stopWebRTC();
  }, [
    isVrMode,
    isVideo,
    isIOLSimulation,
    isProgressiveSimulation,
    peerConnection,
    stopWebRTC,
  ]);

  // refetch exam and device info if signalR is not loading
  useEffect(() => {
    if (!isLoadingSignalR) {
      refetchDevices();
      refetchExams();
    }
  }, [isLoadingSignalR, refetchDevices, refetchExams]);

  useEffect(() => {
    if (
      connection &&
      device?.id &&
      exam &&
      !isLoadingSignalR &&
      connection?.state === HubConnectionState.Connected
    ) {
      if (open) {
        handleSubscribeToDevice(device?.id, connection, refetchExams);
      } else {
        handleUnsubscribeFromDevice(device?.id, connection);
      }
    }
  }, [
    connection,
    open,
    device?.id,
    exam,
    isLoadingSignalR,
    refetchExams,
    handleSubscribeToDevice,
    handleUnsubscribeFromDevice,
  ]);

  return (
    <LightThemeProvider theme={lightTheme}>
      <DefaultModal
        isOpen={open}
        onClose={handleCloseModal}
        fullScreen={showFullScreenModal}
        maxWidth="md"
        title={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxWidth: "fit-content",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                maxWidth: "100%",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {patientName}
            </Typography>
            <Typography sx={{ fontSize: "12px" }}>
              {t("Age")}: {exam?.patient?.age ?? "N/A"}
            </Typography>
          </Box>
        }
        extraHeaderButtons={
          smallerThanSm
            ? []
            : [
                <IconButton
                  sx={{ color: "#FFF" }}
                  onClick={() => setFullScreenModal((prev: boolean) => !prev)}
                >
                  {fullScreenModal ? (
                    <MinimizeIcon aria-label="minimize modal" />
                  ) : (
                    <MaximizeIcon aria-label="maximize modal" />
                  )}
                </IconButton>,
              ]
        }
        modalActions={
          <LiveUpdateButtons
            connection={connection}
            isLoadingSignalR={isLoadingSignalR}
          />
        }
      >
        <Grid height="100%">
          {isLoadingSignalR && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              p="20px"
              width="fit-content"
              borderRadius="8px"
              gap="16px"
              sx={{
                backgroundColor: elevatedSurface,
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 100,
              }}
            >
              <Loader size="24px" containerProps={{ height: "24px" }} />
              <Typography color="#FFF" fontSize="20px" fontWeight={600}>
                {t("Reconnecting...")}
              </Typography>
            </Box>
          )}

          <Grid
            display="flex"
            flexDirection="row"
            width="100%"
            minHeight="100%"
            sx={{ opacity: isLoadingSignalR ? 0.4 : 1 }}
          >
            {/* Multi-package drawer (desktop screen sizes) */}
            {isMultiPackage && <PackageDrawer />}

            <Grid
              display="flex"
              flexDirection="column"
              width="100%"
              sx={{ borderLeft: "1px solid #5B6D7A40" }}
            >
              {/* Modal action buttons and volume slider */}
              <LiveUpdateSettings connection={connection} />

              {/* Error alerts */}
              <LiveUpdateAlerts />

              <Box
                display="flex"
                flexDirection="column"
                gap="8px"
                p="10px 16px"
                flex={1}
                minHeight={0}
              >
                {/* Individual exam information */}

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" flexDirection="column">
                    <Typography fontSize="14px" fontWeight={600}>
                      {currentExamDescription}
                    </Typography>

                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center" gap="8px">
                        <Box display="flex" alignItems="center" gap="4px">
                          <EyeIcon height="16px" width="16px" />
                          <Typography fontSize="12px" fontWeight={600}>
                            {currentEye}
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center" gap="4px">
                          <Typography fontSize="12px" fontWeight={600}>
                            {t("Elapsed Time")}:
                          </Typography>
                          <Typography fontSize="12px">{elapsedTime}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Button
                    disableRipple
                    sx={{
                      ...ModalCustomCancel(),
                      margin: 0,
                      padding: "4px",
                    }}
                    onClick={() => setIsVrMode(!isVrMode)}
                  >
                    <Typography sx={{ fontSize: "12px" }}>
                      {isVrMode
                        ? t("Switch to Exam Mode")
                        : t("Switch to VR Mode")}
                    </Typography>
                  </Button>
                </Box>

                {/* Exam steps and percentage */}
                <LiveUpdateStepper />

                {/* Exam graphs and livestream */}
                <LiveUpdateContent connection={connection} />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </DefaultModal>
      {shouldDisplayNavigation && (
        <SharedModal
          open={shouldDisplayNavigation}
          title={t("View Report")}
          modalDescription={t(
            "The exam is now complete. Would you like to view the report?"
          )}
          onClose={handleCloseModal}
          onConfirm={handleNavigate}
          confirmButtonText={t("View")}
        />
      )}
    </LightThemeProvider>
  );
};

export default LiveUpdateModal;
